import { useTranslation } from 'react-i18next';
import { Text } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import {
  AppDialogSection,
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { INDIVIDUAL_PLANS } from '@/consts/pricing.consts';
import { I18N_KEYS } from '@/language/keys';
import { Flex } from '@/UI/styles';
import { PricingPackage } from '@/components/SubscriptionsAndPayments/payments.types';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { openIntercom } from '@/providers/intercom';
import { BillingCycle, SubscriptionType } from '@/types/shared';
import type { User } from '@/types/auth.types';

interface ConfirmDowngradeModalProps {
  open: boolean;
  onClose: () => void;
  toPackage: PricingPackage;
  onConfirm: (toPackage: PricingPackage) => Promise<void>;
  isProcessing: boolean;
  hasError: boolean;
  currentUser?: User;
}
function ConfirmDowngradeModal({
  open,
  onClose,
  toPackage,
  onConfirm,
  isProcessing,
  hasError,
  currentUser,
}: ConfirmDowngradeModalProps) {
  const { t } = useTranslation();
  const currentSubscription = currentUser?.activeWorkspace.subscription;
  const isMultiMemberTeamToIndividual =
    currentSubscription?.type === SubscriptionType.Team &&
    currentSubscription.usedSeats > 1 &&
    INDIVIDUAL_PLANS.some((plan) => plan === toPackage.name);
  const isAnnualToMonthlyChange =
    currentSubscription?.type === toPackage.name &&
    currentSubscription?.billingCycle === BillingCycle.Yearly &&
    toPackage.billingCycle === BillingCycle.Monthly;
  const cancelButton = (
    <ButtonContained mode={isMultiMemberTeamToIndividual ? 'filled-light' : 'outlined'} onClick={onClose} size="small">
      {t(I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.CTA_CANCEL)}
    </ButtonContained>
  );
  const confirmButton = (
    <ButtonContained
      mode={isMultiMemberTeamToIndividual ? 'outlined' : 'filled-light'}
      startIcon={isProcessing ? <CircularProgress size={16} sx={{ color: token.icon }} /> : null}
      onClick={() => void onConfirm(toPackage)}
      disabled={isProcessing}
      size="small"
    >
      {isProcessing
        ? t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PROCESSING)
        : t(
            isMultiMemberTeamToIndividual
              ? I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.INDIVIDUAL_PLAN_TITLE
              : I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.CTA_CONFIRM,
          )}
    </ButtonContained>
  );

  return (
    <AppDialog open={open} onClose={onClose} width="md" slotProps={{ paper: { sx: { backgroundColor: token.bg } } }}>
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5, pb: 0 }}>
          <AppDialogTitle>
            {t(
              isMultiMemberTeamToIndividual
                ? I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.INDIVIDUAL_PLAN_TITLE
                : I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.TITLE,
            )}
          </AppDialogTitle>
          <AppDialogXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
        </AppDialogHeader>
        <AppDialogContent sx={{ px: 3, pb: 0, '&&': { pt: 2 } }}>
          {hasError && (
            <Flex sx={{ width: '100%', mb: 4, backgroundColor: color.Weavy_Error_08_T, borderRadius: 1, p: 2 }}>
              <TextSmRg color={token.textDanger}>
                <Trans
                  i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE}
                  components={{
                    link: (
                      <ButtonTextPlain onClick={openIntercom} color={token.textDanger} variant="body-sm-rg">
                        {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE_LINK)}
                      </ButtonTextPlain>
                    ),
                  }}
                />
              </TextSmRg>
            </Flex>
          )}
          <Flex sx={{ flexDirection: 'column', gap: 2 }}>
            <TextStdRg color="inherit" sx={{ lineHeight: 'normal' }}>
              <Trans
                i18nKey={
                  isMultiMemberTeamToIndividual
                    ? I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.INDIVIDUAL_PLAN_CHANGE_MESSAGE
                    : isAnnualToMonthlyChange
                      ? I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.MONTHLY_PLAN_CHANGE_MESSAGE
                      : I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.PLAN_CHANGE_MESSAGE
                }
                components={{
                  plan: (
                    <TextStdRg color="inherit" sx={{ textTransform: 'capitalize' }}>
                      {toPackage.name}
                    </TextStdRg>
                  ),
                }}
              />
            </TextStdRg>
            {isMultiMemberTeamToIndividual && (
              <Text.div variant="body-medium">{t(I18N_KEYS.PAYMENTS.DOWNGRADE_MODAL.MEMBER_ACCESS_WARNING)}</Text.div>
            )}
          </Flex>
        </AppDialogContent>
        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 4 }}>
          {isMultiMemberTeamToIndividual ? confirmButton : cancelButton}
          {isMultiMemberTeamToIndividual ? cancelButton : confirmButton}
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

export default ConfirmDowngradeModal;
